import {
  type FC,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Popover, Tooltip } from "antd";
import { DownOutlined, InfoCircleOutlined } from "@ant-design/icons";
import applicationVisibility from "../../../../assets/images/application-visibility.svg";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import TreatmentDetails, {
  type TreatmentDetailsProduct,
} from "../TreatmentDetails";
import ApplicationToggle, {
  type ApplicationToggleOption,
} from "../ApplicationToggle";
import "./treatmentSummarySection.less";

export interface TreatmentSummarySectionTreatment {
  id: string;
  title: string;
  products: TreatmentDetailsProduct[];
  tankVolume: number | null;
  tankVolumeMin?: number;
  tankVolumeMax?: number;
  instructions: string;
}

export interface TreatmentSummarySectionProps {
  treatments: TreatmentSummarySectionTreatment[];
  tankVolumes: Record<string, number | null>;
  onTankVolumeChange: (treatmentId: string, value: number | null) => void;
  applicationOptions: ApplicationToggleOption[];
  applicationValue: string | number;
  onApplicationChange: (value: string | number) => void;
  defaultTreatmentsExpanded?: boolean;
  previousApplicationLabel?: string;
  summaryVisible?: boolean;
  summaryContent?: ReactNode;
  summaryWidth?: number;
  onToggleSummary?: () => void;
}

const TreatmentSummarySection: FC<TreatmentSummarySectionProps> = ({
  treatments,
  tankVolumes,
  onTankVolumeChange,
  applicationOptions,
  applicationValue,
  onApplicationChange,
  defaultTreatmentsExpanded = true,
  previousApplicationLabel,
  summaryVisible = false,
  summaryContent,
  summaryWidth = 280,
  onToggleSummary,
}) => {
  const { t } = useTranslation();
  const [treatmentsExpanded, setTreatmentsExpanded] = useState(
    defaultTreatmentsExpanded
  );
  const summaryAnchorRef = useRef<HTMLDivElement>(null);
  const [summaryOffset, setSummaryOffset] = useState(0);

  useLayoutEffect(() => {
    if (!summaryVisible) return;
    const updatePosition = (): void => {
      const anchor = summaryAnchorRef.current;
      const tabs = anchor
        ?.closest(".rd-tabs-panel")
        ?.querySelector(".ant-tabs-nav");
      if (anchor && tabs) {
        setSummaryOffset(
          tabs.getBoundingClientRect().top - anchor.getBoundingClientRect().top
        );
      }
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [summaryVisible, treatmentsExpanded]);

  return (
    <div
      className="rd-treatment-summary-section"
      data-testid="treatment-summary-section"
    >
      <div className="rd-treatment-summary-section__title-row">
        <span>
          {formatTranslation(t("newRatesAndDosage.cpBiological.tankMix"))}
        </span>
        <Tooltip
          title={formatTranslation(
            t("newRatesAndDosage.cpBiological.tankMixTooltip")
          )}
        >
          <InfoCircleOutlined
            className="rd-treatment-summary-section__info-icon"
            tabIndex={0}
          />
        </Tooltip>
      </div>

      <div className="rd-treatment-summary-section__group">
        <button
          type="button"
          className="rd-treatment-summary-section__group-toggle"
          aria-expanded={treatmentsExpanded}
          onClick={() => {
            setTreatmentsExpanded((prev) => !prev);
          }}
        >
          <DownOutlined
            aria-hidden="true"
            className={`rd-treatment-summary-section__group-toggle-icon ${
              treatmentsExpanded
                ? "rd-treatment-summary-section__group-toggle-icon--expanded"
                : ""
            }`}
          />
          <span>
            {formatTranslation(t("newRatesAndDosage.cpBiological.treatments"))}
          </span>
        </button>
        {treatmentsExpanded && (
          <div className="rd-treatment-summary-section__treatments">
            {treatments.map((treatment) => (
              <TreatmentDetails
                key={treatment.id}
                title={treatment.title}
                products={treatment.products}
                tankVolume={tankVolumes[treatment.id]}
                onTankVolumeChange={(value) => {
                  onTankVolumeChange(treatment.id, value);
                }}
                tankVolumeMin={treatment.tankVolumeMin}
                tankVolumeMax={treatment.tankVolumeMax}
                instructions={treatment.instructions}
              />
            ))}
          </div>
        )}
      </div>

      <ApplicationToggle
        options={applicationOptions}
        value={applicationValue}
        onChange={onApplicationChange}
      />
      {previousApplicationLabel && (
        <Popover
          open={summaryVisible && Boolean(summaryContent)}
          trigger={[]}
          placement="rightTop"
          align={{ offset: [12, summaryOffset] }}
          autoAdjustOverflow={{ adjustX: 0, adjustY: 0 }}
          arrow={false}
          content={summaryContent}
          getPopupContainer={() => document.body}
          overlayClassName="rd-application-summary-popover"
          overlayStyle={{ width: summaryWidth, maxWidth: "calc(100vw - 32px)" }}
          destroyOnHidden
        >
          <div
            ref={summaryAnchorRef}
            className="rd-treatment-summary-section__summary-anchor"
          >
            <button
              type="button"
              className="rd-treatment-summary-section__summary-toggle"
              aria-expanded={summaryVisible}
              onClick={onToggleSummary}
            >
              <span>{previousApplicationLabel}</span>
              <img src={applicationVisibility} alt="" />
            </button>
          </div>
        </Popover>
      )}
    </div>
  );
};

export default TreatmentSummarySection;
